import { useState, useEffect, useCallback, useMemo } from "react";
import { authAxios } from "../utils/auth";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { 
  Star, Trophy, Calendar, FileText, Plus, Edit2, Trash2, 
  ChevronDown, ChevronUp, Filter, Clock, MapPin, Tag,
  Sparkles, CheckCircle2, ArrowRight, Inbox, Check, X,
  HelpCircle, RotateCcw, ArrowUpRight, Merge, Search, Loader2
} from "lucide-react";
import { toast } from "sonner";

// Entry type config
const ENTRY_TYPES = {
  first: { icon: Star, label: "First", color: "text-yellow-400", bg: "bg-yellow-400/20" },
  milestone: { icon: Trophy, label: "Milestone", color: "text-purple-400", bg: "bg-purple-400/20" },
  event: { icon: Calendar, label: "Event", color: "text-blue-400", bg: "bg-blue-400/20" },
  note: { icon: FileText, label: "Note", color: "text-green-400", bg: "bg-green-400/20" },
};

// Queue tag to entry type mapping
const TAG_TO_TYPE = {
  "#FIRST": "first",
  "#MILESTONE": "milestone",
  "#EVENT": "event",
  "#NOTE": "note",
};

const IMPORTANCE_COLORS = {
  low: "border-l-gray-500",
  medium: "border-l-blue-500",
  high: "border-l-yellow-500",
};

// Format date/time for display in user's timezone
const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr) return "No date";
  try {
    let dateObj;
    
    // Check if dateStr already contains time (has 'T' in it)
    if (dateStr.includes('T')) {
      // Already a full datetime string - use directly
      // Add Z for UTC if no timezone specified
      const normalizedDate = dateStr.includes('Z') || dateStr.includes('+') ? dateStr : `${dateStr}Z`;
      dateObj = new Date(normalizedDate);
    } else if (timeStr) {
      // Date only + separate time field
      dateObj = new Date(`${dateStr}T${timeStr}:00Z`);
    } else {
      // Date only, no time
      dateObj = new Date(`${dateStr}T00:00:00Z`);
    }
    
    // Check for invalid date
    if (isNaN(dateObj.getTime())) {
      return dateStr;
    }
    
    // Format in user's local timezone
    const options = { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    };
    
    return dateObj.toLocaleString(undefined, options);
  } catch (e) {
    return `${dateStr} ${timeStr || ''}`;
  }
};

// Format just the date portion
const formatDateOnly = (dateStr) => {
  try {
    const dateObj = new Date(dateStr + 'T00:00:00');
    return dateObj.toLocaleDateString(undefined, { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch (e) {
    return dateStr;
  }
};

// Help Modal for explaining actions
const ActionsHelpModal = ({ open, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-blue-400" />
          Timeline & Queue Actions
        </DialogTitle>
        <DialogDescription className="text-white/60">
          Manage your relationship memories
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div>
          <h4 className="font-medium text-white mb-2">Timeline Actions</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li className="flex items-start gap-2">
              <ArrowUpRight className="w-4 h-4 mt-0.5 text-green-400 shrink-0" />
              <span><strong>Sim→Real:</strong> Promote simulated entry to real (actually happened IRL)</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowUpRight className="w-4 h-4 mt-0.5 text-blue-400 shrink-0 rotate-180" />
              <span><strong>Real→Sim:</strong> Move real entry back to simulated (was marked real by mistake)</span>
            </li>
            <li className="flex items-start gap-2">
              <RotateCcw className="w-4 h-4 mt-0.5 text-orange-400 shrink-0" />
              <span><strong>Re-Queue:</strong> Send back to review queue for reconsideration</span>
            </li>
            <li className="flex items-start gap-2">
              <Merge className="w-4 h-4 mt-0.5 text-purple-400 shrink-0" />
              <span><strong>Merge:</strong> Combine selected entries into one (uses most recent title/date, combines descriptions)</span>
            </li>
            <li className="flex items-start gap-2">
              <Trash2 className="w-4 h-4 mt-0.5 text-red-400 shrink-0" />
              <span><strong>Delete:</strong> Permanently remove from timeline</span>
            </li>
          </ul>
        </div>
        
        <div className="border-t border-white/10 pt-4">
          <h4 className="font-medium text-white mb-2">Queue Actions</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 text-green-400 shrink-0" />
              <span><strong>Approve:</strong> Add to timeline as a confirmed memory</span>
            </li>
            <li className="flex items-start gap-2">
              <Trash2 className="w-4 h-4 mt-0.5 text-red-400 shrink-0" />
              <span><strong>Delete:</strong> Reject and remove from queue</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowUpRight className="w-4 h-4 mt-0.5 text-green-400 shrink-0" />
              <span><strong>Approve as Real:</strong> Approve and mark as real (happened IRL)</span>
            </li>
          </ul>
        </div>
        
        <div className="border-t border-white/10 pt-4 text-xs text-white/50">
          <p><strong>Simulated:</strong> Events from persona chat roleplay</p>
          <p><strong>Real:</strong> Events that actually happened in real life</p>
        </div>
      </div>
      
      <DialogFooter>
        <Button onClick={onClose} className="w-full">Got it</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Merge Instructions Modal - shown when multiple HIGH priority records selected
const MergeInstructionsModal = ({ open, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="bg-[#1a1625] border border-white/10 text-white max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-orange-400">
          <Merge className="w-5 h-5" />
          Merge Instructions
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4 text-sm">
        <p className="text-white/80">
          Cannot merge multiple <span className="text-red-400 font-semibold">HIGH</span> priority records. 
          The highest priority record becomes the master.
        </p>
        
        <div className="bg-white/5 rounded-lg p-4 space-y-3">
          <p className="font-semibold text-white">To merge records:</p>
          <ol className="list-decimal list-inside space-y-2 text-white/70">
            <li>Set <span className="text-red-400 font-semibold">ONE</span> timeline record as <span className="text-red-400">High</span> priority (this becomes the master)</li>
            <li>Select lower priority records (<span className="text-yellow-400">Medium</span>/<span className="text-green-400">Low</span>) to merge into the master</li>
            <li>The master keeps its title, date, time, and location</li>
            <li>Other records' notes are added to the master's description</li>
          </ol>
        </div>
      </div>
      
      <DialogFooter>
        <Button onClick={onClose} className="w-full bg-divine-gold text-black hover:bg-divine-gold/80">
          Got it
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Format date for display - handles both YYYY-MM-DD and full ISO datetime
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    // Normalize date string - add time if missing to avoid timezone issues
    let normalizedDate = dateStr;
    if (dateStr.length === 10) {
      // Date-only format (YYYY-MM-DD) - add midnight time
      normalizedDate = `${dateStr}T00:00:00`;
    }
    const date = new Date(normalizedDate);
    if (isNaN(date.getTime())) {
      return dateStr; // Return original if parsing fails
    }
    return date.toLocaleDateString("en-US", { 
      weekday: "short", 
      year: "numeric", 
      month: "short", 
      day: "numeric" 
    });
  } catch {
    return dateStr;
  }
};

// Entry Card Component
const TimelineEntry = ({ entry, onEdit, onDelete, selected, onToggleSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const typeConfig = ENTRY_TYPES[entry.entry_type] || ENTRY_TYPES.event;
  const TypeIcon = typeConfig.icon;
  
  return (
    <div 
      className={`bg-white/5 rounded-lg border-l-4 ${IMPORTANCE_COLORS[entry.importance]} 
                  hover:bg-white/10 transition-colors cursor-pointer
                  ${selected ? 'ring-2 ring-divine-gold/50 bg-divine-gold/5' : ''}`}
      data-testid={`timeline-entry-${entry.entry_id}`}
      onClick={() => onToggleSelect(entry.entry_id)}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Selection checkbox - always visible */}
          <div className="pt-1">
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect(entry.entry_id)}
              onClick={(e) => e.stopPropagation()}
              className="border-white/30 data-[state=checked]:bg-divine-gold data-[state=checked]:border-divine-gold"
            />
          </div>
          
          {/* Type Icon */}
          <div className={`p-2 rounded-lg ${typeConfig.bg}`}>
            <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-white font-medium">{entry.title}</h4>
              <span className={`text-xs px-2 py-0.5 rounded ${typeConfig.bg} ${typeConfig.color}`}>
                {typeConfig.label}
              </span>
              {entry.source === "state_analyzer" && (
                <span className="text-xs px-2 py-0.5 rounded bg-orange-400/20 text-orange-400">
                  AI
                </span>
              )}
              {entry.source === "auto_captured" && (
                <span className="text-xs px-2 py-0.5 rounded bg-green-400/20 text-green-400">
                  Auto
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-4 mt-1 text-sm text-white/50">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDateTime(entry.date, entry.time)}
              </span>
              {entry.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {entry.location}
                </span>
              )}
            </div>
          </div>
          
          {/* Expand toggle */}
          {/* Expand/Edit buttons */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              className="text-white/30 hover:text-white/60 p-1"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-white/10 pt-3" onClick={(e) => e.stopPropagation()}>
          {entry.description && (
            <p className="text-white/70 text-sm whitespace-pre-wrap mb-3">
              {entry.description}
            </p>
          )}
          
          {entry.tags && entry.tags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <Tag className="w-3 h-3 text-white/40" />
              {entry.tags.map((tag) => (
                <span 
                  key={tag} 
                  className="text-xs px-2 py-0.5 bg-white/10 rounded text-white/60"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {entry.mood && (
            <p className="text-sm text-white/50 mb-3">
              Mood: <span className="text-white/70">{entry.mood}</span>
            </p>
          )}
          
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onEdit(entry); }}
              className="text-white/50 hover:text-white"
              data-testid={`edit-entry-${entry.entry_id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDelete(entry.entry_id); }}
              className="text-red-400/50 hover:text-red-400"
              data-testid={`delete-entry-${entry.entry_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Button Component
const StatButton = ({ value, label, color, filter, onClick }) => (
  <button
    onClick={() => onClick(filter)}
    className="hover:bg-white/10 rounded-lg p-2 transition-colors cursor-pointer"
    data-testid={`stat-${filter}`}
  >
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-white/50">{label}</p>
  </button>
);

// Stats Card - Updated for simulated/real separation + queued count
const StatsCard = ({ stats, realityType, queuedCount, onFilterChange, matchName }) => {
  if (!stats) return null;
  
  const currentStats = realityType === "real" ? stats.real : stats.simulated;
  const otherStats = realityType === "real" ? stats.simulated : stats.real;
  const otherLabel = realityType === "real" ? "Simulated" : "Real";
  
  // Format first date for display
  const formatFirstDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      let normalizedDate = dateStr;
      if (dateStr.length === 10) {
        normalizedDate = `${dateStr}T00:00:00`;
      }
      const date = new Date(normalizedDate);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString("en-US", { 
        month: "short", 
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return null;
    }
  };
  
  const firstDate = formatFirstDate(currentStats?.first_date);
  const daysSince = currentStats?.days_since_first;
  
  return (
    <div className="bg-white/5 rounded-lg p-4 mb-4">
      <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
        <StatButton 
          value={currentStats?.total || 0} 
          label="Total Entries" 
          color="text-white" 
          filter="all"
          onClick={onFilterChange}
        />
        <StatButton 
          value={currentStats?.by_type?.first || 0} 
          label="Firsts" 
          color="text-yellow-400" 
          filter="first"
          onClick={onFilterChange}
        />
        <StatButton 
          value={currentStats?.by_type?.milestone || 0} 
          label="Milestones" 
          color="text-purple-400" 
          filter="milestone"
          onClick={onFilterChange}
        />
        <StatButton 
          value={currentStats?.by_type?.event || 0} 
          label="Events" 
          color="text-blue-400" 
          filter="event"
          onClick={onFilterChange}
        />
        <StatButton 
          value={currentStats?.by_type?.note || 0} 
          label="Notes" 
          color="text-green-400" 
          filter="note"
          onClick={onFilterChange}
        />
        <StatButton 
          value={queuedCount || 0} 
          label="Review Queue" 
          color={queuedCount > 0 ? 'text-orange-400' : 'text-white/30'} 
          filter="queued"
          onClick={onFilterChange}
        />
      </div>
      
      {/* Relationship duration subscript */}
      {matchName && daysSince !== null && daysSince !== undefined && firstDate && (
        <div className="mt-3 pt-3 border-t border-white/10 text-center">
          <p className="text-sm text-white/60">
            You have been in a Relationship with <span className="text-divine-gold font-medium">{matchName}</span> {daysSince} Days Since {firstDate}
          </p>
        </div>
      )}
      
      {/* Show other type count as hint */}
      {otherStats?.total > 0 && (
        <div className="mt-2 text-center">
          <p className="text-xs text-white/40">
            {otherStats.total} {otherLabel} {otherStats.total === 1 ? 'entry' : 'entries'} available
          </p>
        </div>
      )}
    </div>
  );
};

// Queued Entry Card for Review - with checkbox
const QueuedEntry = ({ item, selected, onToggleSelect }) => {
  const entryType = TAG_TO_TYPE[item.tag] || "event";
  const typeConfig = ENTRY_TYPES[entryType];
  const TypeIcon = typeConfig.icon;
  
  // Extract text after the tag
  const entryText = item.entry.replace(/^#(FIRST|MILESTONE|EVENT|NOTE)\s*/i, "");
  
  // Format created_at in user timezone
  const createdDate = item.created_at ? new Date(item.created_at).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : '';
  
  return (
    <div 
      className={`bg-white/5 rounded-lg border-l-4 border-l-orange-400 p-4 transition-colors cursor-pointer ${
        selected ? 'bg-orange-500/10 ring-1 ring-orange-400/50' : 'hover:bg-white/10'
      }`}
      onClick={() => onToggleSelect(item.queue_id)}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div className="pt-1">
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect(item.queue_id)}
            onClick={(e) => e.stopPropagation()}
            className="border-white/30 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
          />
        </div>
        
        <div className={`p-2 rounded-lg ${typeConfig.bg}`}>
          <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full ${typeConfig.bg} ${typeConfig.color}`}>
              {typeConfig.label}
            </span>
            <span className="text-xs text-white/40">P{item.priority}</span>
            {createdDate && (
              <span className="text-xs text-white/30">{createdDate}</span>
            )}
          </div>
          <p className="text-white/90 text-sm">{entryText}</p>
        </div>
      </div>
    </div>
  );
};

// Entry Form Dialog - Updated with reality_type
const EntryFormDialog = ({ open, onClose, entry, matchId, onSaved, defaultRealityType }) => {
  const isEdit = !!entry && entry.entry_id; // entry_id present means editing existing
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    entry_type: "event",
    reality_type: defaultRealityType || "simulated",
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    time: "",
    location: "",
    tags: "",
    mood: "",
    importance: "medium",
  });
  
  useEffect(() => {
    if (entry && entry.entry_id) {
      // Editing existing entry
      // Extract date part from datetime string if needed
      let dateValue = entry.date || new Date().toISOString().split("T")[0];
      if (dateValue.includes('T')) {
        dateValue = dateValue.split('T')[0];
      }
      // Extract time part if not provided separately
      let timeValue = entry.time || "";
      if (!timeValue && entry.date && entry.date.includes('T')) {
        const timePart = entry.date.split('T')[1];
        if (timePart) {
          timeValue = timePart.substring(0, 5); // HH:MM
        }
      }
      
      setForm({
        entry_type: entry.entry_type || "event",
        reality_type: entry.reality_type || "simulated",
        title: entry.title || "",
        description: entry.description || "",
        date: dateValue,
        time: timeValue,
        location: entry.location || "",
        tags: (entry.tags || []).join(", "),
        mood: entry.mood || "",
        importance: entry.importance || "medium",
      });
    } else if (entry && entry.entry_type) {
      // Quick add with pre-set entry type (e.g., "first")
      setForm({
        entry_type: entry.entry_type,
        reality_type: defaultRealityType || "simulated",
        title: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
        time: "",
        location: "",
        tags: "",
        mood: "",
        importance: entry.entry_type === "first" ? "high" : "medium",
      });
    } else {
      // New entry with defaults
      setForm({
        entry_type: "event",
        reality_type: defaultRealityType || "simulated",
        title: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
        time: "",
        location: "",
        tags: "",
        mood: "",
        importance: "medium",
      });
    }
  }, [entry, open, defaultRealityType]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      
      if (isEdit) {
        await authAxios.put(`/matches/${matchId}/timeline/${entry.entry_id}`, payload);
        toast.success("Entry updated");
      } else {
        await authAxios.post(`/matches/${matchId}/timeline`, payload);
        toast.success("Entry added");
      }
      
      onSaved();
      onClose();
    } catch (error) {
      // Handle validation errors (Pydantic returns array of errors)
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
        toast.error(errorMsg || "Validation error");
      } else {
        toast.error(detail || "Failed to save entry");
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Entry" : "Add Timeline Entry"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Entry Type */}
          <div>
            <label className="text-sm text-white/60 mb-1 block">Type</label>
            <Select value={form.entry_type} onValueChange={(v) => setForm({...form, entry_type: v})}>
              <SelectTrigger className="bg-white/5 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-white/10">
                {Object.entries(ENTRY_TYPES).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <config.icon className={`w-4 h-4 ${config.color}`} />
                      {config.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Title */}
          <div>
            <label className="text-sm text-white/60 mb-1 block">Title *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              placeholder="First date at the park..."
              className="bg-white/5 border-white/10"
              data-testid="timeline-entry-title"
            />
          </div>
          
          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-white/60 mb-1 block">Date *</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({...form, date: e.target.value})}
                className="bg-white/5 border-white/10"
                data-testid="timeline-entry-date"
              />
            </div>
            <div>
              <label className="text-sm text-white/60 mb-1 block">Time</label>
              <Input
                type="time"
                value={form.time}
                onChange={(e) => setForm({...form, time: e.target.value})}
                className="bg-white/5 border-white/10"
              />
            </div>
          </div>
          
          {/* Location */}
          <div>
            <label className="text-sm text-white/60 mb-1 block">Location</label>
            <Input
              value={form.location}
              onChange={(e) => setForm({...form, location: e.target.value})}
              placeholder="Central Park, NYC"
              className="bg-white/5 border-white/10"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="text-sm text-white/60 mb-1 block">Description</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              placeholder="What happened? How did you feel?"
              className="bg-white/5 border-white/10 min-h-[80px]"
              data-testid="timeline-entry-description"
            />
          </div>
          
          {/* Tags & Mood */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-white/60 mb-1 block">Tags (comma separated)</label>
              <Input
                value={form.tags}
                onChange={(e) => setForm({...form, tags: e.target.value})}
                placeholder="romantic, outdoors"
                className="bg-white/5 border-white/10"
              />
            </div>
            <div>
              <label className="text-sm text-white/60 mb-1 block">Mood</label>
              <Input
                value={form.mood}
                onChange={(e) => setForm({...form, mood: e.target.value})}
                placeholder="excited, nervous"
                className="bg-white/5 border-white/10"
              />
            </div>
          </div>
          
          {/* Importance */}
          <div>
            <label className="text-sm text-white/60 mb-1 block">Importance</label>
            <Select value={form.importance} onValueChange={(v) => setForm({...form, importance: v})}>
              <SelectTrigger className="bg-white/5 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-white/10">
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-divine-gold text-black hover:bg-divine-gold/80"
              data-testid="save-timeline-entry"
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Add Entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Suggested Firsts Section
// Quick Add Firsts Modal
const QuickAddFirstsModal = ({ open, onClose, matchId, realityType, onAdded }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingItem, setProcessingItem] = useState(null);
  
  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/matches/${matchId}/timeline/suggestions/firsts`);
      setSuggestions(res.data.suggestions || []);
    } catch (error) {
      console.error("Failed to fetch suggestions:", error);
    } finally {
      setLoading(false);
    }
  }, [matchId]);
  
  useEffect(() => {
    if (open) {
      fetchSuggestions();
    }
  }, [open, fetchSuggestions]);
  
  const handleToggle = async (suggestion) => {
    setProcessingItem(suggestion.title);
    
    try {
      if (suggestion.already_logged && suggestion.entry_id) {
        // Unlog - delete the entry
        await authAxios.delete(`/matches/${matchId}/timeline/${suggestion.entry_id}`);
        toast.success(`Removed: ${suggestion.title}`);
      } else {
        // Log - add the entry
        await authAxios.post(`/matches/${matchId}/timeline`, {
          entry_type: suggestion.entry_type,
          reality_type: realityType,
          title: suggestion.title,
          importance: suggestion.importance,
          date: new Date().toISOString().split("T")[0],
        });
        toast.success(`Added: ${suggestion.title}`);
      }
      fetchSuggestions();
      onAdded();
    } catch (error) {
      toast.error(suggestion.already_logged ? "Failed to remove entry" : "Failed to add entry");
    } finally {
      setProcessingItem(null);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1625] border border-white/10 text-white max-w-md">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-white/50 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-yellow-400">
            <Star className="w-5 h-5" />
            Quick Add Firsts
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-2 mt-4">
          {loading ? (
            <div className="text-center py-8 text-white/50">Loading...</div>
          ) : (
            suggestions.map((s) => (
              <button
                key={s.title}
                onClick={() => handleToggle(s)}
                disabled={processingItem === s.title}
                className={`w-full text-left p-3 rounded-lg text-sm transition-all flex items-center gap-3 ${
                  processingItem === s.title
                    ? "bg-yellow-400/20 text-yellow-400 opacity-50"
                    : s.already_logged 
                    ? "bg-green-400/10 text-green-400 hover:bg-red-400/10 hover:text-red-400" 
                    : "bg-white/5 hover:bg-yellow-400/20 text-white/70 hover:text-yellow-400"
                }`}
              >
                <span className={`w-5 h-5 flex items-center justify-center rounded-full border transition-colors ${
                  s.already_logged 
                    ? "border-green-400 bg-green-400/20" 
                    : "border-white/20"
                }`}>
                  {s.already_logged && <Check className="w-3 h-3" />}
                </span>
                {s.title}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Timeline Tab Component
const TimelineTab = ({ matchId, matchName }) => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [realityType, setRealityType] = useState("real"); // "simulated" or "real" — defaults to real per 2026-07-13 spec
  const [filterType, setFilterType] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  
  // User persona name for heading
  const [userPersonaName, setUserPersonaName] = useState("You");
  
  // Selection state for timeline entries
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");
  const [processingBulk, setProcessingBulk] = useState(false);
  
  // Selection state for queue items
  const [selectedQueueIds, setSelectedQueueIds] = useState(new Set());
  const [queueBulkAction, setQueueBulkAction] = useState("");
  
  // Help modal
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  
  // Merge instructions modal
  const [showMergeInstructions, setShowMergeInstructions] = useState(false);
  
  // Quick Add Firsts modal
  const [quickAddFirstsOpen, setQuickAddFirstsOpen] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Review Queue state
  const [queuedItems, setQueuedItems] = useState([]);
  const [queueTotalCount, setQueueTotalCount] = useState(0);
  const [queueLoading, setQueueLoading] = useState(false);
  
  // Fetch review queue for this match - filtered by reality type
  const fetchQueue = useCallback(async () => {
    try {
      // Fetch with match_id and reality_type filter
      const res = await authAxios.get(`/state-analyzer/memory-queue?match_id=${matchId}&reality_type=${realityType}`);
      setQueuedItems(res.data.pending || []);
      setQueueTotalCount(res.data.total_count || 0);
    } catch (error) {
      console.error("Failed to fetch queue:", error);
    }
  }, [matchId, realityType]);  // Added realityType dependency
  
  const fetchEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams({ 
        sort_order: sortOrder,
        reality_type: realityType
      });
      if (filterType !== "all" && filterType !== "queued") {
        params.append("entry_type", filterType);
      }
      
      const res = await authAxios.get(`/matches/${matchId}/timeline?${params}`);
      setEntries(res.data.entries || []);
      setStats(res.data.stats || null);
      // Set user persona name for heading
      if (res.data.user_persona_name) {
        setUserPersonaName(res.data.user_persona_name);
      }
    } catch (error) {
      console.error("Failed to fetch timeline:", error);
      toast.error(error.response?.data?.detail || "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }, [matchId, filterType, sortOrder, realityType]);
  
  // Filter entries by search query (client-side)
  const filteredEntries = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return entries;
    }
    
    const query = debouncedSearch.toLowerCase();
    return entries.filter(entry => {
      // Search in title
      if (entry.title?.toLowerCase().includes(query)) return true;
      // Search in description/notes
      if (entry.description?.toLowerCase().includes(query)) return true;
      // Search in location
      if (entry.location?.toLowerCase().includes(query)) return true;
      // Search in tags
      if (entry.tags?.some(tag => tag.toLowerCase().includes(query))) return true;
      return false;
    });
  }, [entries, debouncedSearch]);
  
  useEffect(() => {
    setLoading(true);
    fetchEntries();
    fetchQueue();
  }, [fetchEntries, fetchQueue]);
  
  // Reset selection and search when switching filters or reality type
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectedQueueIds(new Set());
    setBulkAction("");
    setQueueBulkAction("");
    setSearchQuery("");
  }, [realityType, filterType]);
  
  // Toggle timeline entry selection
  const handleToggleSelect = (entryId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };
  
  // Toggle queue item selection
  const handleToggleQueueSelect = (queueId) => {
    setSelectedQueueIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(queueId)) {
        newSet.delete(queueId);
      } else {
        newSet.add(queueId);
      }
      return newSet;
    });
  };
  
  // Select all / deselect all for timeline (uses filteredEntries)
  const handleSelectAllTimeline = () => {
    if (selectedIds.size === filteredEntries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEntries.map(e => e.entry_id)));
    }
  };
  
  // Select all / deselect all for queue
  const handleSelectAllQueue = () => {
    if (selectedQueueIds.size === queuedItems.length) {
      setSelectedQueueIds(new Set());
    } else {
      setSelectedQueueIds(new Set(queuedItems.map(q => q.queue_id)));
    }
  };
  
  // Execute bulk action on timeline entries
  const handleTimelineBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    
    const ids = Array.from(selectedIds);
    
    // Confirm delete action
    if (bulkAction === "delete") {
      if (!confirm(`Are you sure you want to delete ${ids.length} timeline ${ids.length === 1 ? 'entry' : 'entries'}? This cannot be undone.`)) {
        return;
      }
    }
    
    setProcessingBulk(true);
    
    try {
      if (bulkAction === "delete") {
        // Delete with error tolerance - some items may already be deleted
        let successCount = 0;
        let failCount = 0;
        for (const id of ids) {
          try {
            await authAxios.delete(`/matches/${matchId}/timeline/${id}`);
            successCount++;
          } catch (err) {
            // Item may already be deleted or not found - continue
            failCount++;
          }
        }
        if (successCount > 0) {
          toast.success(`Deleted ${successCount} entries${failCount > 0 ? ` (${failCount} not found)` : ''}`);
        } else {
          toast.error("No entries were deleted - they may have already been removed");
        }
      } else if (bulkAction === "promote") {
        await authAxios.post(`/matches/${matchId}/timeline/promote-to-real`, {
          entry_ids: ids
        });
        toast.success(`Promoted ${ids.length} entries to Real`);
      } else if (bulkAction === "demote") {
        await authAxios.post(`/matches/${matchId}/timeline/demote-to-simulated`, {
          entry_ids: ids
        });
        toast.success(`Moved ${ids.length} entries back to Simulated`);
      } else if (bulkAction === "requeue") {
        await authAxios.post(`/matches/${matchId}/timeline/requeue`, {
          entry_ids: ids
        });
        toast.success(`Moved ${ids.length} entries back to review queue`);
      } else if (bulkAction === "merge") {
        if (ids.length < 2) {
          toast.error("Select at least 2 entries to merge");
          return;
        }
        try {
          await authAxios.post(`/matches/${matchId}/timeline/merge`, {
            entry_ids: ids
          });
          toast.success(`Merged ${ids.length} entries into 1`);
        } catch (mergeError) {
          if (mergeError.response?.data?.detail === "MULTIPLE_HIGH_PRIORITY") {
            setShowMergeInstructions(true);
            return;
          }
          throw mergeError;
        }
      } else if (bulkAction === "set_high" || bulkAction === "set_med" || bulkAction === "set_low") {
        const priorityMap = { set_high: "high", set_med: "medium", set_low: "low" };
        await authAxios.post(`/matches/${matchId}/timeline/set-priority`, {
          entry_ids: ids,
          priority: priorityMap[bulkAction]
        });
        toast.success(`Set ${ids.length} entries to ${priorityMap[bulkAction]} priority`);
      }
      
      setSelectedIds(new Set());
      setBulkAction("");
      fetchEntries();
    } catch (error) {
      toast.error(`Failed to ${bulkAction}`);
    } finally {
      setProcessingBulk(false);
    }
  };
  
  // Execute bulk action on queue items
  const handleQueueBulkAction = async () => {
    if (!queueBulkAction || selectedQueueIds.size === 0) return;
    
    const ids = Array.from(selectedQueueIds);
    
    // Confirm delete action
    if (queueBulkAction === "delete") {
      if (!confirm(`Are you sure you want to delete ${ids.length} queued ${ids.length === 1 ? 'item' : 'items'}? This cannot be undone.`)) {
        return;
      }
    }
    
    setQueueLoading(true);
    
    try {
      if (queueBulkAction === "approve") {
        for (const id of ids) {
          await authAxios.post(`/state-analyzer/memory-queue/${id}/confirm`);
        }
        toast.success(`Approved ${ids.length} items`);
      } else if (queueBulkAction === "delete") {
        // Delete with error tolerance - some items may already be processed
        let successCount = 0;
        let failCount = 0;
        for (const id of ids) {
          try {
            await authAxios.post(`/state-analyzer/memory-queue/${id}/reject`);
            successCount++;
          } catch (err) {
            // Item may already be processed or not found - continue
            failCount++;
          }
        }
        if (successCount > 0) {
          toast.success(`Deleted ${successCount} items${failCount > 0 ? ` (${failCount} already processed)` : ''}`);
        } else {
          toast.error("No items were deleted - they may have already been processed");
        }
      } else if (queueBulkAction === "approve_real") {
        for (const id of ids) {
          await authAxios.post(`/state-analyzer/memory-queue/${id}/confirm`, {
            reality_type: "real"
          });
        }
        toast.success(`Approved ${ids.length} items as Real`);
      } else if (queueBulkAction === "merge_approve") {
        if (ids.length < 2) {
          toast.error("Select at least 2 items to merge");
          setQueueLoading(false);
          return;
        }
        await authAxios.post(`/state-analyzer/memory-queue/merge`, {
          queue_ids: ids
        });
        toast.success(`Merged and approved ${ids.length} items`);
      } else if (queueBulkAction === "set_high" || queueBulkAction === "set_med" || queueBulkAction === "set_low") {
        const priorityMap = { set_high: "high", set_med: "medium", set_low: "low" };
        await authAxios.post(`/state-analyzer/memory-queue/set-priority`, {
          queue_ids: ids,
          priority: priorityMap[queueBulkAction]
        });
        toast.success(`Set ${ids.length} items to ${priorityMap[queueBulkAction]} priority`);
      }
      
      setSelectedQueueIds(new Set());
      setQueueBulkAction("");
      fetchQueue();
      fetchEntries();
    } catch (error) {
      toast.error(`Failed to ${queueBulkAction}`);
    } finally {
      setQueueLoading(false);
    }
  };
  
  const handleDelete = async (entryId) => {
    if (!confirm("Delete this timeline entry?")) return;
    
    try {
      await authAxios.delete(`/matches/${matchId}/timeline/${entryId}`);
      toast.success("Entry deleted");
      fetchEntries();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };
  
  const handleEdit = (entry) => {
    setEditEntry(entry);
    setDialogOpen(true);
  };
  
  const handleAddNew = () => {
    setEditEntry(null);
    setDialogOpen(true);
  };
  
  const handleQuickAddFirst = () => {
    setQuickAddFirstsOpen(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="timeline-tab">
      {/* Section Heading */}
      <div className="text-center">
        <h2 className="text-lg font-semibold text-divine-gold font-cinzel">
          {matchName} and {userPersonaName}&apos;s history together
        </h2>
      </div>
      
      {/* Reality Type Toggle */}
      <div className="flex justify-center gap-2 p-1 bg-white/5 rounded-lg max-w-md mx-auto">
        <button
          onClick={() => setRealityType("simulated")}
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            realityType === "simulated" 
              ? "bg-purple-500/30 text-purple-300 shadow-lg shadow-purple-500/20" 
              : "text-white/50 hover:text-white/70 hover:bg-white/5"
          }`}
          data-testid="toggle-simulated"
        >
          <Sparkles className="w-4 h-4" />
          Simulated
          {stats?.simulated?.total > 0 && (
            <span className="text-xs bg-purple-500/30 px-2 py-0.5 rounded-full">
              {stats.simulated.total}
            </span>
          )}
        </button>
        <button
          onClick={() => setRealityType("real")}
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            realityType === "real" 
              ? "bg-green-500/30 text-green-300 shadow-lg shadow-green-500/20" 
              : "text-white/50 hover:text-white/70 hover:bg-white/5"
          }`}
          data-testid="toggle-real"
        >
          <CheckCircle2 className="w-4 h-4" />
          Real
          {stats?.real?.total > 0 && (
            <span className="text-xs bg-green-500/30 px-2 py-0.5 rounded-full">
              {stats.real.total}
            </span>
          )}
        </button>
      </div>
      
      {/* Stats */}
      <StatsCard 
        stats={stats} 
        realityType={realityType} 
        queuedCount={queueTotalCount} 
        onFilterChange={setFilterType}
        matchName={matchName}
      />
      
      {/* Actions Bar - Desktop: Single row | Mobile: Two rows with reordered controls */}
      {/* Mobile Row 1: Quick Add First + Entry */}
      <div className="flex md:hidden items-center justify-end gap-2 mb-2">
        {/* Quick Add Firsts - only show when not in queue view */}
        {filterType !== "queued" && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleQuickAddFirst}
            className="border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/10 shrink-0"
          >
            <Star className="w-4 h-4 mr-2" />
            Quick Add First
          </Button>
        )}
        
        {/* Add Entry */}
        <Button
          onClick={handleAddNew}
          className="bg-divine-gold text-black hover:bg-divine-gold/80 shrink-0"
          data-testid="add-timeline-entry-mobile"
        >
          <Plus className="w-4 h-4 mr-2" />
          Entry
        </Button>
      </div>
      
      {/* Mobile Row 2: Sort + Search */}
      <div className="flex md:hidden items-center gap-2 mb-2">
        {/* Sort */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
          className="border-white/10 text-white/70 shrink-0"
        >
          {sortOrder === "desc" ? "Newest First" : "Oldest First"}
        </Button>
        
        {/* Help button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setHelpModalOpen(true)}
          className="text-white/50 hover:text-white shrink-0"
        >
          <HelpCircle className="w-4 h-4" />
        </Button>
        
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-8 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-9"
            data-testid="timeline-search-input-mobile"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Desktop: Original single row layout */}
      <div className="hidden md:flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          {/* Sort */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
            className="border-white/10 text-white/70 shrink-0"
          >
            {sortOrder === "desc" ? "Newest First" : "Oldest First"}
          </Button>
          
          {/* Help button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHelpModalOpen(true)}
            className="text-white/50 hover:text-white shrink-0"
          >
            <HelpCircle className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Search Input - Center */}
        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            type="text"
            placeholder="Search timeline..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-8 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-9"
            data-testid="timeline-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Quick Add Firsts - only show when not in queue view */}
          {filterType !== "queued" && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleQuickAddFirst}
              className="border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/10 shrink-0"
            >
              <Star className="w-4 h-4 mr-2" />
              Quick Add First
            </Button>
          )}
          
          {/* Add Entry */}
          <Button
            onClick={handleAddNew}
            className="bg-divine-gold text-black hover:bg-divine-gold/80 shrink-0"
            data-testid="add-timeline-entry"
          >
            <Plus className="w-4 h-4 mr-2" />
            Entry
          </Button>
        </div>
      </div>
      
      {/* Actions Bar - Row 2: Selection count, Bulk Actions */}
      {filterType === "queued" ? (
        // Show Review Queue
        <div className="space-y-3">
          {queuedItems.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No items pending review</p>
              <p className="text-sm mt-1">
                State Analyzer will queue new memories here for your approval
              </p>
            </div>
          ) : (
            <>
              {/* Queue Bulk Actions Bar */}
              <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedQueueIds.size === queuedItems.length && queuedItems.length > 0}
                    onCheckedChange={handleSelectAllQueue}
                    className="border-white/30 data-[state=checked]:bg-orange-500"
                  />
                  <span className="text-sm text-white/70">
                    {selectedQueueIds.size > 0 
                      ? `${selectedQueueIds.size} of ${queuedItems.length} selected`
                      : `${queuedItems.length} pending`
                    }
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Select value={queueBulkAction} onValueChange={setQueueBulkAction}>
                    <SelectTrigger className="w-[160px] bg-white/5 border-white/10 h-8">
                      <SelectValue placeholder="Action..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10">
                      <SelectItem value="approve">
                        <span className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-green-400" /> Approve
                        </span>
                      </SelectItem>
                      <SelectItem value="delete">
                        <span className="flex items-center gap-2">
                          <Trash2 className="w-3 h-3 text-red-400" /> Delete
                        </span>
                      </SelectItem>
                      <SelectItem value="approve_real">
                        <span className="flex items-center gap-2">
                          <ArrowUpRight className="w-3 h-3 text-green-400" /> Approve as Real
                        </span>
                      </SelectItem>
                      <SelectItem value="merge_approve">
                        <span className="flex items-center gap-2">
                          <Merge className="w-3 h-3 text-purple-400" /> Merge & Approve
                        </span>
                      </SelectItem>
                      <SelectItem value="set_high">
                        <span className="flex items-center gap-2">
                          <Star className="w-3 h-3 text-red-400" /> Set High
                        </span>
                      </SelectItem>
                      <SelectItem value="set_med">
                        <span className="flex items-center gap-2">
                          <Star className="w-3 h-3 text-yellow-400" /> Set Med
                        </span>
                      </SelectItem>
                      <SelectItem value="set_low">
                        <span className="flex items-center gap-2">
                          <Star className="w-3 h-3 text-green-400" /> Set Low
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button
                    size="sm"
                    onClick={handleQueueBulkAction}
                    disabled={selectedQueueIds.size === 0 || !queueBulkAction || queueLoading}
                    className="bg-divine-gold text-black hover:bg-divine-gold/80"
                  >
                    {queueLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {queueBulkAction === "delete" ? "Deleting History..." : "Processing..."}
                      </>
                    ) : "Apply"}
                  </Button>
                </div>
              </div>
              
              {/* Queue Items */}
              {queuedItems.map((item) => (
                <QueuedEntry
                  key={item.queue_id}
                  item={item}
                  selected={selectedQueueIds.has(item.queue_id)}
                  onToggleSelect={handleToggleQueueSelect}
                />
              ))}
            </>
          )}
        </div>
      ) : (
        // Show Timeline Entries
        entries.length === 0 ? (
          <div className="text-center py-12 text-white/50">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No {realityType} timeline entries yet</p>
            <p className="text-sm mt-1">
              {realityType === "simulated" 
                ? `Start documenting your simulated journey with ${matchName}` 
                : `Record real moments with ${matchName}`
              }
            </p>
          </div>
        ) : filteredEntries.length === 0 ? (
          // No search results
          <div className="text-center py-12 text-white/50">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No results found for &ldquo;{debouncedSearch}&rdquo;</p>
            <p className="text-sm mt-1">
              Try a different search term
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchQuery("")}
              className="mt-3 text-divine-gold hover:text-divine-gold/80"
            >
              Clear search
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Timeline Bulk Actions Bar */}
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedIds.size === filteredEntries.length && filteredEntries.length > 0}
                  onCheckedChange={handleSelectAllTimeline}
                  className="border-white/30 data-[state=checked]:bg-divine-gold"
                />
                <span className="text-sm text-white/70">
                  {selectedIds.size > 0 
                    ? `${selectedIds.size} of ${filteredEntries.length} selected`
                    : debouncedSearch 
                      ? `${filteredEntries.length} of ${entries.length} entries`
                      : `${filteredEntries.length} entries`
                  }
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Select value={bulkAction} onValueChange={setBulkAction}>
                  <SelectTrigger className="w-[160px] bg-white/5 border-white/10 h-8">
                    <SelectValue placeholder="Action..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-white/10">
                    {realityType === "simulated" && (
                      <SelectItem value="promote">
                        <span className="flex items-center gap-2">
                          <ArrowUpRight className="w-3 h-3 text-green-400" /> Sim→Real
                        </span>
                      </SelectItem>
                    )}
                    {realityType === "real" && (
                      <SelectItem value="demote">
                        <span className="flex items-center gap-2">
                          <ArrowUpRight className="w-3 h-3 text-blue-400 rotate-180" /> Real→Sim
                        </span>
                      </SelectItem>
                    )}
                    <SelectItem value="requeue">
                      <span className="flex items-center gap-2">
                        <RotateCcw className="w-3 h-3 text-orange-400" /> Re-Queue
                      </span>
                    </SelectItem>
                    <SelectItem value="merge">
                      <span className="flex items-center gap-2">
                        <Merge className="w-3 h-3 text-purple-400" /> Merge
                      </span>
                    </SelectItem>
                    <SelectItem value="set_high">
                      <span className="flex items-center gap-2">
                        <Star className="w-3 h-3 text-red-400" /> Set High
                      </span>
                    </SelectItem>
                    <SelectItem value="set_med">
                      <span className="flex items-center gap-2">
                        <Star className="w-3 h-3 text-yellow-400" /> Set Med
                      </span>
                    </SelectItem>
                    <SelectItem value="set_low">
                      <span className="flex items-center gap-2">
                        <Star className="w-3 h-3 text-green-400" /> Set Low
                      </span>
                    </SelectItem>
                    <SelectItem value="delete">
                      <span className="flex items-center gap-2">
                        <Trash2 className="w-3 h-3 text-red-400" /> Delete
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  size="sm"
                  onClick={handleTimelineBulkAction}
                  disabled={selectedIds.size === 0 || !bulkAction || processingBulk}
                  className="bg-divine-gold text-black hover:bg-divine-gold/80"
                >
                  {processingBulk ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {bulkAction === "delete" ? "Deleting History..." : "Processing..."}
                    </>
                  ) : "Apply"}
                </Button>
              </div>
            </div>
            
            {/* Timeline Entries */}
            {filteredEntries.map((entry) => (
              <TimelineEntry
                key={entry.entry_id}
                entry={entry}
                onEdit={handleEdit}
                onDelete={handleDelete}
                selected={selectedIds.has(entry.entry_id)}
                onToggleSelect={handleToggleSelect}
              />
            ))}
          </div>
        )
      )}
      
      {/* Help Modal */}
      <ActionsHelpModal open={helpModalOpen} onClose={() => setHelpModalOpen(false)} />
      
      {/* Merge Instructions Modal */}
      <MergeInstructionsModal open={showMergeInstructions} onClose={() => setShowMergeInstructions(false)} />
      
      {/* Quick Add Firsts Modal */}
      <QuickAddFirstsModal
        open={quickAddFirstsOpen}
        onClose={() => setQuickAddFirstsOpen(false)}
        matchId={matchId}
        realityType={realityType}
        onAdded={fetchEntries}
      />
      
      {/* Entry Form Dialog */}
      <EntryFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        entry={editEntry}
        matchId={matchId}
        onSaved={fetchEntries}
        defaultRealityType={realityType}
      />
    </div>
  );
};

export default TimelineTab;
